// Captures beforeinstallprompt at module load, before React renders.
//
// Chrome fires this event once, early in page load. The first version of this
// code listened inside InstallLink/InstallPanel — components that mount well
// after load, and on authenticated routes much later — so the event had
// already come and gone and nothing ever held it. The panel then rendered
// null everywhere, which is exactly what driving dev showed.
//
// Imported for side effects from main.tsx so the listener exists before the
// first render, and kept in module scope so every component sees the same
// captured event rather than racing for its own.
import type { InstallPromptEvent } from './installState';

type Listener = () => void;

let captured: InstallPromptEvent | null = null;
let installed = false;
const listeners = new Set<Listener>();

const notify = () => listeners.forEach((l) => l());

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e: Event) => {
    // Stop Chrome's own mini-infobar so the offer appears where we choose.
    e.preventDefault();
    captured = e as InstallPromptEvent;
    notify();
  });
  window.addEventListener('appinstalled', () => {
    captured = null;
    installed = true;
    notify();
  });
}

export const getCapturedPrompt = (): InstallPromptEvent | null => captured;
export const wasInstalledThisSession = (): boolean => installed;

/** Consumes the event: it is single-use, so a second prompt() does nothing. */
export const consumePrompt = (): InstallPromptEvent | null => {
  const e = captured;
  captured = null;
  if (e) notify();
  return e;
};

export const subscribeToInstallState = (fn: Listener): (() => void) => {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
};
